import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, UserButton, useUser } from "@clerk/clerk-react";
import {
  Organisation,
  TaskDefinition,
  TaskPlan,
  TaskType,
  Team,
  User as TPUser,
} from "../types";
import { getEditorContext, savePlan } from "../services/storageService";
import { generateId } from "../services/utils";
import { ErrorModal, LimitReachedModal } from "./Modals";
import { RichTextEditor } from "./RichTextEditor";
import ShareModal from "./ShareModal";
import TimelineView from "./TimelineView";
import PlanFlowEditor from "./PlanFlowEditor";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Cpu,
  GanttChartSquare,
  GripVertical,
  Heading,
  Info,
  List,
  Plus,
  Share2,
  Shield,
  SkipForward,
  Trash2,
  Users as UsersIcon,
  Workflow,
  X,
  Zap,
} from "./Icons";
import { ChevronRight, Github, SkipBack } from "lucide-react";
import AutomationConfigModal from "./AutomationConfigModal";
import GithubInstallModal from "./GithubInstallModal";
import { UpgradePrompt } from "./UpgradePrompt";

interface PlanEditorProps {
  currentOrg: Organisation;
  onClose: () => void;
}
const getTaskNumber = (tasks: TaskDefinition[], taskId: string) => {
  let count = 0;
  for (const t of tasks) {
    if (t.type !== TaskType.SECTION) {
      count++;
    }
    if (t.id === taskId) {
      return t.type === TaskType.SECTION ? null : count;
    }
  }
  return null;
};

const AssigneeCombobox: React.FC<{
  users: TPUser[];
  teams: Team[];
  assignees?: { type: "USER" | "TEAM"; id: string }[];
  onChange: (assignees: { type: "USER" | "TEAM"; id: string }[]) => void;
}> = ({ users, teams, assignees = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleAssignee = (type: "USER" | "TEAM", id: string) => {
    const exists = assignees.find((a) => a.type === type && a.id === id);
    if (exists) {
      onChange(assignees.filter((a) => !(a.type === type && a.id === id)));
    } else {
      onChange([...assignees, { type, id }]);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(filter.toLowerCase()) ||
      u.email.toLowerCase().includes(filter.toLowerCase()),
  );

  const filteredTeams = teams.filter((t) =>
    t.name.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        className="flex flex-wrap gap-1 items-center px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 cursor-pointer hover:border-indigo-500 transition-colors min-h-[42px]"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="task-assignees-trigger"
      >
        {assignees.length === 0 ? (
          <span className="text-xs text-slate-400">Unassigned</span>
        ) : (
          assignees.map((a) => {
            if (a.type === "USER") {
              const user = users.find((u) => u.id === a.id);
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[10px] font-medium border border-indigo-100 dark:border-indigo-800"
                >
                  <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center bg-indigo-200">
                    {user?.imageUrl ? (
                      <img
                        src={user.imageUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      user?.initials || "U"
                    )}
                  </div>
                  {user?.name || "Unknown"}
                </div>
              );
            } else {
              const team = teams.find((t) => t.id === a.id);
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded text-[10px] font-medium border border-purple-100 dark:border-purple-800"
                >
                  <UsersIcon size={10} /> {team?.name}
                </div>
              );
            }
          })
        )}
        <ChevronDown size={12} className="ml-auto text-slate-400 shrink-0" />
      </div>

      {isOpen && (
        <div className="absolute z-dropdown w-64 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-80 overflow-y-auto right-0">
          <div className="p-2 border-b border-slate-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800">
            <input
              autoFocus
              className="w-full text-xs p-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none text-slate-700 dark:text-slate-200"
              placeholder="Search people or teams..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          <div className="p-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Teams
          </div>
          {filteredTeams.map((t) => {
            const isSelected = assignees.find(
              (a) => a.type === "TEAM" && a.id === t.id,
            );
            return (
              <button
                key={t.id}
                type="button"
                data-testid="assignee-option"
                className={`w-full text-left flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${isSelected ? "bg-indigo-50/50 dark:bg-indigo-900/20" : ""}`}
                onClick={() => toggleAssignee("TEAM", t.id)}
              >
                <UsersIcon size={14} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                  {t.name}
                </span>
                {isSelected && (
                  <X size={12} className="ml-auto text-indigo-500" />
                )}
              </button>
            );
          })}

          <div className="p-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-t border-slate-100 dark:border-slate-700">
            People
          </div>
          {filteredUsers.map((u) => {
            const isSelected = assignees.find(
              (a) => a.type === "USER" && a.id === u.id,
            );
            return (
              <button
                key={u.id}
                type="button"
                data-testid="assignee-option"
                className={`w-full text-left flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${isSelected ? "bg-indigo-50/50 dark:bg-indigo-900/20" : ""}`}
                onClick={() => toggleAssignee("USER", u.id)}
              >
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-white font-bold overflow-hidden bg-indigo-500">
                  {u.imageUrl ? (
                    <img
                      src={u.imageUrl}
                      alt={u.initials}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    u.initials
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                    {u.name}
                  </span>
                  <span className="text-[10px] text-slate-400">{u.email}</span>
                </div>
                {isSelected && (
                  <X size={12} className="ml-auto text-indigo-500" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DependencyCombobox: React.FC<{
  allTasks: TaskDefinition[];
  currentTask: TaskDefinition;
  onChange: (newIds: string[]) => void;
}> = ({ allTasks, currentTask, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const availableTasks = allTasks.filter(
    (t) =>
      t.id !== currentTask.id &&
      t.type !== TaskType.SECTION &&
      !currentTask.dependencyIds.includes(t.id) &&
      t.name.toLowerCase().includes(filter.toLowerCase()),
  );

  const selectedTasks = allTasks.filter((t) =>
    currentTask.dependencyIds.includes(t.id),
  );

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex flex-wrap gap-1 min-h-[42px] p-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500 overflow-hidden">
        {selectedTasks.map((t) => (
          <span
            key={t.id}
            data-testid="dependency-tag"
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 max-w-[250px]"
            title={t.name}
          >
            <span className="truncate">
              {getTaskNumber(allTasks, t.id)}. {t.name}
            </span>
            <button
              onClick={() =>
                onChange(currentTask.dependencyIds.filter((id) => id !== t.id))
              }
              className="ml-1 text-slate-400 hover:text-red-500 shrink-0"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-1 min-w-[120px] text-sm p-1 outline-none bg-transparent text-slate-900 dark:text-slate-100 placeholder-slate-400"
          placeholder={
            selectedTasks.length === 0 ? "Select dependencies..." : ""
          }
          value={filter}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setFilter(e.target.value);
            setIsOpen(true);
          }}
        />
      </div>

      {isOpen && availableTasks.length > 0 && (
        <div className="absolute z-dropdown w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {availableTasks.map((t) => (
            <button
              key={t.id}
              data-testid="dependency-item"
              className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
              onClick={() => {
                onChange([...currentTask.dependencyIds, t.id]);
                setFilter("");
              }}
            >
              <span className="text-slate-400 font-mono text-xs">
                #{getTaskNumber(allTasks, t.id)}
              </span>
              <span className="truncate">{t.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InfoPopover: React.FC<{ content: string }> = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative flex items-center normal-case"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Info
        size={13}
        className="text-slate-400 hover:text-indigo-500 cursor-help transition-colors"
      />
      {isOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg z-popover text-center pointer-events-none">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
        </div>
      )}
    </div>
  );
};

const MetaDivider = () => (
  <span className="text-slate-300 dark:text-slate-600 select-none">•</span>
);

const PlanEditor: React.FC<PlanEditorProps> = ({ currentOrg, onClose }) => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [viewMode, setViewMode] = useState<"LIST" | "TIMELINE" | "FLOW">(
    "LIST",
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prerequisites, setPrerequisites] = useState<string[]>([]);
  const [tasks, setTasks] = useState<TaskDefinition[]>([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [advancedExpandedTasks, setAdvancedExpandedTasks] = useState<
    Set<string>
  >(new Set());
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<TPUser[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [limitReachedMessage, setLimitReachedMessage] = useState<string | null>(
    null,
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [fullPlan, setFullPlan] = useState<TaskPlan | null>(null);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [githubSetupModalOpen, setGithubSetupModalOpen] = useState(false);
  const [currentTaskForAutomation, setCurrentTaskForAutomation] =
    useState<TaskDefinition | null>(null);
  const [estimatedStartTime, setEstimatedStartTime] = useState<
    number | undefined
  >(undefined);

  const [isPlanLoading, setIsPlanLoading] = useState(!!planId);
  const [isContextLoading, setIsContextLoading] = useState(true);
  const isLoading = isPlanLoading || isContextLoading;

  const isMaintainer = currentOrg.role === "MAINTAINER";
  const isTrialExpired =
    currentOrg.subscriptionStatus === "TRIALING" &&
    (currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now();
  const isReadOnly = isTrialExpired;

  const userTeamIds = useMemo(() => {
    if (!user || !allTeams) return new Set<string>();
    return new Set(
      allTeams.filter((t) => t.memberIds?.includes(user.id)).map((t) => t.id),
    );
  }, [user, allTeams]);

  const canEdit = useMemo(() => {
    if (isReadOnly) return false;
    if (isMaintainer) return true;
    if (!fullPlan || !user) return false;
    if (fullPlan.ownerId === user.id) return true;

    return fullPlan.shares?.some((s) => {
      if (s.permission !== "EDITOR") return false;
      if (s.granteeType === "USER" && s.granteeId === user.id) return true;
      if (s.granteeType === "ORGANISATION" && s.granteeId === currentOrg.id)
        return true;
      if (s.granteeType === "TEAM" && userTeamIds.has(s.granteeId)) return true;
      return false;
    });
  }, [isMaintainer, fullPlan, user, currentOrg.id, userTeamIds, isReadOnly]);

  const fetchContextRef = useRef(false);

  const fetchContext = useCallback(async () => {
    // Avoid multiple concurrent calls
    if (fetchContextRef.current) return;
    fetchContextRef.current = true;

    setIsPlanLoading(true);
    setIsContextLoading(true);
    try {
      const token = await getToken();
      const data = await getEditorContext(
        currentOrg.id,
        planId,
        token || undefined,
      );

      setAllUsers(data.members);
      setAllTeams(data.teams);

      if (data.plan) {
        setFullPlan(data.plan);
        setName(data.plan.name);
        setDescription(data.plan.description);
        setPrerequisites(data.plan.prerequisites || []);
        setTasks(data.plan.tasks || []);
        setEstimatedStartTime(data.plan.estimatedStartTime);
      }
    } catch (e) {
      setErrorMessage("Failed to load plan details and context.");
    } finally {
      setIsPlanLoading(false);
      setIsContextLoading(false);
      fetchContextRef.current = false;
    }
  }, [currentOrg.id, planId, getToken]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  const handleSave = async () => {
    if (!canEdit) return;
    if (!name) {
      setErrorMessage("Plan name is required");
      return;
    }
    const token = await getToken();
    const newPlan: TaskPlan = {
      id: planId || generateId(),
      organisationId: currentOrg.id,
      name,
      description,
      prerequisites,
      createdAt: Date.now(),
      tasks,
      estimatedStartTime,
    };
    try {
      await savePlan(newPlan, token || undefined);
      onClose();
    } catch (e: any) {
      if (e.message?.includes("limit")) {
        setLimitReachedMessage(e.message);
      } else {
        setErrorMessage(e.message || "Failed to save plan");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col animate-pulse">
        <div className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800"></div>
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1 h-16 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
              <div className="md:col-span-2 h-16 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  const addTask = (type: TaskType = TaskType.MANUAL) => {
    if (!canEdit) return;
    const newTask: TaskDefinition = {
      id: generateId(),
      name: "",
      description: "",
      type: type,
      durationMinutes: type === TaskType.SECTION ? 0 : 15,
      dependencyIds: [],
      assignees: [],
    };
    setTasks([...tasks, newTask]);
    setExpandedTasks(new Set([newTask.id]));
    setFocusTaskId(newTask.id);
  };

  const updateTask = (id: string, updates: Partial<TaskDefinition>) => {
    if (!canEdit) return;
    setTasks(tasks.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  };

  const removeTask = (id: string) => {
    if (!canEdit) return;
    setTasks(tasks.filter((t) => t.id !== id));
    setTasks((prev) =>
      prev.map((t) => ({
        ...t,
        dependencyIds: t.dependencyIds.filter((depId) => depId !== id),
      })),
    );
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setAdvancedExpandedTasks((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAdvancedExpand = (id: string) => {
    setAdvancedExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAutomationSave = (config: {
    githubRepo: string;
    githubWorkflow: string;
    githubBranch: string;
    githubInputs?: Record<string, any>;
  }) => {
    if (!currentTaskForAutomation) return;
    updateTask(currentTaskForAutomation.id, {
      type: TaskType.AUTOMATED,
      ...config,
      assignees: [], // Clear assignees
    });
    setAutomationModalOpen(false);
    setCurrentTaskForAutomation(null);
  };

  const handleAutomationRevert = () => {
    if (!currentTaskForAutomation) return;
    updateTask(currentTaskForAutomation.id, { type: TaskType.MANUAL });
    setAutomationModalOpen(false);
    setCurrentTaskForAutomation(null);
  };

  // Drag and Drop Handlers
  const handleDragStart = (index: number) => {
    if (!isMaintainer) return;
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (
      !isMaintainer ||
      draggedItemIndex === null ||
      draggedItemIndex === index
    )
      return;

    const newTasks = [...tasks];
    const draggedItem = newTasks[draggedItemIndex];
    newTasks.splice(draggedItemIndex, 1);
    newTasks.splice(index, 0, draggedItem);

    setTasks(newTasks);
    setDraggedItemIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const tpUser: TPUser | null = user
    ? {
        id: user.id,
        name: user.fullName || "User",
        email: user.primaryEmailAddress?.emailAddress || "",
        initials: user.firstName?.[0] || "U",
        organisationIds: [],
      }
    : null;

  return (
    <div className="h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200 flex flex-col overflow-hidden">
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        message={errorMessage || ""}
      />
      <LimitReachedModal
        isOpen={!!limitReachedMessage}
        onClose={() => setLimitReachedMessage(null)}
        message={limitReachedMessage || ""}
        onUpgrade={() => navigate(`/${currentOrg.slug}/workspace/billing`)}
      />
      {fullPlan && showShareModal && (
        <ShareModal
          plan={fullPlan}
          currentOrg={currentOrg}
          onClose={() => setShowShareModal(false)}
          onUpdate={fetchContext}
        />
      )}
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-header backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={onClose}
          >
            <div className="bg-indigo-600 rounded-lg p-1.5">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">
              taskplan
            </span>
            <span className="text-slate-300 dark:text-slate-600 mx-2">/</span>
            <span className="font-medium text-slate-600 dark:text-slate-300">
              {planId ? (canEdit ? "Edit Plan" : "View Plan") : "New Workflow"}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="hidden lg:inline text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
            >
              {canEdit ? "Cancel" : "Back"}
            </button>
            {planId && canEdit && (
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
              >
                <Share2 size={16} />
                <span className="hidden lg:inline">Share</span>
              </button>
            )}
            {canEdit && (
              <button
                onClick={handleSave}
                data-testid="save-plan-button"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors"
                title="Save Changes"
              >
                <span className="hidden lg:inline">Save Changes</span>
                <span className="lg:hidden">Save</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Editor Body */}
      <div
        className={`flex-1 bg-slate-50 dark:bg-slate-950 flex flex-col ${viewMode === "FLOW" ? "overflow-hidden" : "overflow-y-auto"}`}
      >
        {/* Meta Info Area */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  data-testid="plan-name-input"
                  className="block w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-white rounded-lg shadow-sm p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-75 disabled:cursor-not-allowed"
                  placeholder="e.g., Monthly System Maintenance"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canEdit}
                  placeholder="Brief description of this workflow"
                  className="block w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-white rounded-lg shadow-sm p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-75 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="mt-6 border-t border-slate-100 dark:border-slate-800 pt-4">
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                Pre-run Checklist
              </label>
              <div className="space-y-2">
                {prerequisites.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newPrereqs = [...prerequisites];
                        newPrereqs[idx] = e.target.value;
                        setPrerequisites(newPrereqs);
                      }}
                      disabled={!canEdit}
                      className="flex-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-white rounded-lg shadow-sm p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-75"
                      placeholder="Checklist item..."
                    />
                    {canEdit && (
                      <button
                        onClick={() => {
                          const newPrereqs = prerequisites.filter(
                            (_, i) => i !== idx,
                          );
                          setPrerequisites(newPrereqs);
                        }}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button
                    onClick={() => setPrerequisites([...prerequisites, ""])}
                    className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    <Plus size={14} /> Add Checklist Item
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 sticky top-0 z-sticky">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
            <div className="flex gap-3">
              {canEdit ? (
                <>
                  <button
                    onClick={() => addTask(TaskType.SECTION)}
                    className="flex items-center gap-2 text-sm bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 sm:px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 shadow-sm transition font-medium"
                  >
                    <Heading size={16} />
                    <span className="hidden lg:inline">Add Section</span>
                    <span className="lg:hidden">Section</span>
                  </button>
                  <button
                    onClick={() => addTask(TaskType.MANUAL)}
                    className="flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg shadow-sm transition font-medium"
                  >
                    <Plus size={16} />
                    <span className="hidden lg:inline">Add Task</span>
                    <span className="lg:hidden">Task</span>
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
                  <Shield size={16} className="text-slate-400" /> Read-only
                  access
                </div>
              )}
            </div>

            <div className="flex bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 p-1 shadow-sm">
              <button
                onClick={() => setViewMode("LIST")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "LIST" ? "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <List size={14} /> List
              </button>
              <button
                onClick={() => setViewMode("TIMELINE")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "TIMELINE" ? "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <GanttChartSquare size={14} /> Timeline
              </button>
              <button
                onClick={() => setViewMode("FLOW")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "FLOW" ? "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <Workflow size={14} /> Flow
              </button>
            </div>
          </div>
        </div>

        <div
          className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${viewMode === "FLOW" ? "flex-1 min-h-0 flex flex-col" : ""}`}
        >
          {/* Plan Limit Up-sell */}
          {currentOrg.subscriptionStatus === "FREE" && !planId && (
            <div className="mb-6">
              <UpgradePrompt
                currentOrg={currentOrg}
                title="Create Better Plans"
                message="Free workspaces are limited to 5 plans. Upgrade to Team to create unlimited plans and collaborate with your whole team."
                onUpgrade={() =>
                  navigate(`/${currentOrg.slug}/workspace/billing`)
                }
              />
            </div>
          )}
          {tasks.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
              <Cpu
                size={48}
                className="mx-auto mb-4 text-slate-300 dark:text-slate-700"
              />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                Start Building
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">
                Create tasks manually or automate them with GitHub. Drag and
                drop to reorder.
              </p>
              {isMaintainer && (
                <button
                  onClick={() => addTask(TaskType.MANUAL)}
                  className="mt-6 text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                >
                  Add your first task
                </button>
              )}
            </div>
          ) : viewMode === "TIMELINE" ? (
            <div className="h-[600px]">
              <TimelineView
                tasks={tasks}
                estimatedStartTime={estimatedStartTime}
                onUpdateEstimatedStartTime={setEstimatedStartTime}
              />
            </div>
          ) : viewMode === "FLOW" ? (
            <div className="flex-1 min-h-0 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm bg-slate-50 dark:bg-slate-900">
              <PlanFlowEditor
                tasks={tasks}
                onTasksChange={setTasks}
                readOnly={!canEdit}
              />
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task, index) => {
                const isExpanded = expandedTasks.has(task.id);
                const isSection = task.type === TaskType.SECTION;
                const taskNumber = getTaskNumber(tasks, task.id);

                return (
                  <div
                    key={task.id}
                    draggable={isMaintainer}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    data-testid="task-row"
                    className={`w-full border rounded-xl bg-white dark:bg-slate-900 relative group transition-all duration-200 hover:shadow-md ${draggedItemIndex === index ? "opacity-50 ring-2 ring-indigo-500" : ""} ${isSection ? "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50" : "border-slate-200 dark:border-slate-800"} ${focusTaskId === task.id ? "ring-1 ring-indigo-500/50" : ""}`}
                  >
                    <div
                      className={`flex items-center gap-4 px-4 pt-4 pb-3 ${!isSection && !isExpanded ? "cursor-pointer" : ""}`}
                      onClick={() => {
                        if (!isSection && !isExpanded) {
                          toggleExpand(task.id);
                        }
                      }}
                    >
                      {isMaintainer && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-grab active:cursor-grabbing text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 flex items-center justify-center w-6"
                        >
                          <GripVertical size={16} />
                        </div>
                      )}

                      <div className="w-8 text-xs font-mono text-slate-400 dark:text-slate-600 text-center select-none font-bold">
                        {isSection ? "" : taskNumber}
                      </div>

                      <div className="flex-1 min-w-0" title={task.name}>
                        <input
                          type="text"
                          autoFocus={focusTaskId === task.id}
                          value={task.name}
                          onChange={(e) =>
                            updateTask(task.id, { name: e.target.value })
                          }
                          disabled={!isMaintainer}
                          data-testid="task-name-input"
                          onFocus={() => {
                            setFocusTaskId(task.id);
                            if (!expandedTasks.has(task.id) && !isSection) {
                              setExpandedTasks((prev) =>
                                new Set(prev).add(task.id),
                              );
                            }
                          }}
                          className={`block w-full bg-transparent focus:outline-none disabled:cursor-default ${isSection ? "font-bold text-lg text-slate-800 dark:text-slate-100" : "font-medium text-base text-slate-700 dark:text-slate-200"}`}
                          placeholder={
                            isSection ? "Section Header" : "Task Name"
                          }
                        />
                        {!isExpanded && !isSection && (
                          <div className="hidden md:flex items-center gap-1 mt-0.5 ml-0.5 animate-in fade-in duration-200">
                            <div className="flex items-center gap-1 text-xs text-slate-400 font-medium">
                              <Clock size={10} />
                              <span>
                                {task.durationStrategy === "FIXED_END_TIME" ? (
                                  <span className="italic">
                                    Ends at {task.fixedEndTime?.time || "?"}
                                  </span>
                                ) : (
                                  <>
                                    {Math.floor(task.durationMinutes / 60) > 0
                                      ? `${Math.floor(task.durationMinutes / 60)}h `
                                      : ""}
                                    {task.durationMinutes % 60 > 0
                                      ? `${task.durationMinutes % 60}m`
                                      : ""}
                                  </>
                                )}
                              </span>
                            </div>
                            {task.type === TaskType.AUTOMATED && (
                              <>
                                <MetaDivider />
                                <div className="flex items-center gap-1 text-xs text-purple-800 dark:text-purple-400 font-bold uppercase tracking-wider">
                                  <Cpu size={10} />
                                  <span>Automated</span>
                                </div>
                              </>
                            )}
                            {task.assignees && task.assignees.length > 0 && (
                              <>
                                <MetaDivider />
                                <div
                                  className="flex items-center gap-1"
                                  data-testid="task-assignees"
                                >
                                  {task.assignees.map((a, i) => {
                                    const u =
                                      a.type === "USER"
                                        ? allUsers.find((u) => u.id === a.id)
                                        : null;
                                    const t =
                                      a.type === "TEAM"
                                        ? allTeams.find((t) => t.id === a.id)
                                        : null;
                                    return (
                                      <div
                                        key={i}
                                        className="min-w-4 max-w-36 py-0.5 px-1.5 truncate rounded flex items-center gap-1 text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
                                        title={u?.name || t?.name || "Assignee"}
                                      >
                                        {a.type === "USER" ? (
                                          <>
                                            <div className="w-3.5 h-3.5 rounded-full overflow-hidden flex-shrink-0 bg-indigo-500 flex items-center justify-center text-[6px] text-white">
                                              {u?.imageUrl ? (
                                                <img
                                                  src={u.imageUrl}
                                                  className="w-full h-full object-cover"
                                                />
                                              ) : (
                                                u?.initials || "U"
                                              )}
                                            </div>
                                            <span className="truncate">
                                              {u?.name || "Unknown"}
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <UsersIcon size={10} />
                                            <span className="truncate">
                                              {t?.name || "Team"}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            {task.dependencyIds &&
                              task.dependencyIds.length > 0 && (
                                <>
                                  <MetaDivider />
                                  <div className="flex items-center gap-1 text-xs text-slate-400 font-medium">
                                    <span>
                                      Depends on:{" "}
                                      {task.dependencyIds
                                        .map(
                                          (id) =>
                                            `#${getTaskNumber(tasks, id)}`,
                                        )
                                        .join(", ")}
                                    </span>
                                  </div>
                                </>
                              )}
                          </div>
                        )}
                      </div>

                      {/* Header End */}

                      <div className="flex items-center gap-1 border-l border-slate-200 dark:border-slate-800 pl-4">
                        {isMaintainer && !isSection && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (currentOrg.subscriptionStatus === "FREE") {
                                setErrorMessage(
                                  "GitHub Actions automation is only available on Team and Enterprise plans. Upgrade your workspace to automate this task.",
                                );
                                return;
                              }
                              if (!currentOrg.githubInstallationId) {
                                setGithubSetupModalOpen(true);
                                return;
                              }
                              setCurrentTaskForAutomation(task);
                              setAutomationModalOpen(true);
                            }}
                            title={
                              currentOrg.subscriptionStatus === "FREE"
                                ? "Upgrade to use Automation"
                                : task.type === TaskType.MANUAL
                                  ? "Automate this task"
                                  : "Determine Automation Config"
                            }
                            className={`p-2 rounded-full transition ${task.type === TaskType.AUTOMATED ? "text-purple-600 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40" : "text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                          >
                            <Zap size={18} />
                          </button>
                        )}
                        {!isSection && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(task.id);
                            }}
                            data-testid="expand-task-button"
                            className="p-2 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          >
                            {isExpanded ? (
                              <ChevronUp size={18} />
                            ) : (
                              <ChevronDown size={18} />
                            )}
                          </button>
                        )}
                        {isMaintainer && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTask(task.id);
                            }}
                            title="Delete Task"
                            className="p-2 text-slate-400 hover:text-red-500 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && !isSection && (
                      <div className="p-6 border-t border-slate-100 dark:border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50/30 dark:bg-slate-800/20 rounded-b-xl">
                        <div className="space-y-6">
                          <div className="h-full">
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                              Description
                            </label>
                            <RichTextEditor
                              content={task.description}
                              onChange={(val) =>
                                updateTask(task.id, {
                                  description: val,
                                })
                              }
                              disabled={!isMaintainer}
                              placeholder="Add instructions for the task..."
                              className="flex-1"
                            />
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                                Duration
                              </label>
                              {task.durationStrategy === "FIXED_END_TIME" ? (
                                <button
                                  onClick={() => {
                                    if (!advancedExpandedTasks.has(task.id)) {
                                      toggleAdvancedExpand(task.id);
                                    }
                                    setTimeout(() => {
                                      document
                                        .getElementById(`end-time-${task.id}`)
                                        ?.focus();
                                    }, 100);
                                  }}
                                  className="w-full h-[42px] flex items-center justify-center text-sm text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors shadow-sm group/dur"
                                >
                                  Ends at {task.fixedEndTime?.time || "??:??"}
                                  <ChevronRight
                                    size={14}
                                    className="ml-1 opacity-0 group-hover/dur:opacity-100 transition-all group-hover/dur:translate-x-0.5"
                                  />
                                </button>
                              ) : (
                                <div
                                  className={`flex bg-white dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-700 items-center gap-2 h-[42px] ${!isMaintainer ? "opacity-80 pointer-events-none" : ""}`}
                                >
                                  <div className="flex items-center gap-1 flex-1 justify-center border-r border-slate-100 dark:border-slate-800 pr-2">
                                    <input
                                      type="number"
                                      min="0"
                                      placeholder="0"
                                      value={
                                        Math.floor(task.durationMinutes / 60) ||
                                        ""
                                      }
                                      onChange={(e) => {
                                        const hours =
                                          parseInt(e.target.value) || 0;
                                        const minutes =
                                          task.durationMinutes % 60;
                                        updateTask(task.id, {
                                          durationMinutes: hours * 60 + minutes,
                                        });
                                      }}
                                      disabled={!isMaintainer}
                                      className="w-full text-right bg-transparent outline-none text-sm font-medium text-slate-700 dark:text-slate-200 placeholder-slate-300"
                                    />
                                    <span className="text-slate-400 text-xs font-bold">
                                      HR
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 flex-1 justify-center pl-1">
                                    <input
                                      type="number"
                                      min="0"
                                      max="59"
                                      placeholder="0"
                                      value={task.durationMinutes % 60 || ""}
                                      onChange={(e) => {
                                        const hours = Math.floor(
                                          task.durationMinutes / 60,
                                        );
                                        const minutes =
                                          parseInt(e.target.value) || 0;
                                        updateTask(task.id, {
                                          durationMinutes: hours * 60 + minutes,
                                        });
                                      }}
                                      disabled={!isMaintainer}
                                      className="w-full text-right bg-transparent outline-none text-sm font-medium text-slate-700 dark:text-slate-200 placeholder-slate-300"
                                    />
                                    <span className="text-slate-400 text-xs font-bold mr-2">
                                      MIN
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              {task.type === TaskType.MANUAL ? (
                                <div>
                                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                                    Assignees
                                  </label>
                                  <div
                                    className={
                                      !isMaintainer
                                        ? "pointer-events-none opacity-80"
                                        : ""
                                    }
                                  >
                                    <AssigneeCombobox
                                      users={allUsers}
                                      teams={allTeams}
                                      assignees={task.assignees}
                                      onChange={(val) =>
                                        updateTask(task.id, { assignees: val })
                                      }
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                                    Automation
                                  </label>
                                  <div
                                    className={`bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 ${isMaintainer ? "cursor-pointer" : "pointer-events-none opacity-80"}`}
                                    onClick={() => {
                                      if (!isMaintainer) return;
                                      setCurrentTaskForAutomation(task);
                                      setAutomationModalOpen(true);
                                    }}
                                  >
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                                        <span className="text-slate-400 flex items-center justify-center w-4">
                                          <Github size={12} />
                                        </span>
                                        <span className="font-mono truncate">
                                          {task.githubWorkflow ||
                                            "No workflow file"}
                                          {task.githubBranch &&
                                            task.githubBranch !== "main" && (
                                              <span className="ml-1 opacity-50">
                                                @ {task.githubBranch}
                                              </span>
                                            )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2 flex items-center gap-1">
                              Dependencies{" "}
                              <InfoPopover content="Tasks will be blocked until all dependencies are completed." />
                            </label>
                            <div
                              className={
                                !isMaintainer
                                  ? "pointer-events-none opacity-80"
                                  : ""
                              }
                            >
                              <DependencyCombobox
                                allTasks={tasks}
                                currentTask={task}
                                onChange={(ids) =>
                                  updateTask(task.id, { dependencyIds: ids })
                                }
                              />
                            </div>
                          </div>

                          <div className="space-y-4">
                            <button
                              onClick={() => toggleAdvancedExpand(task.id)}
                              className="w-full flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 uppercase select-none transition-colors"
                            >
                              <span>Advanced Settings</span>
                              <ChevronRight
                                size={12}
                                className={`transition-transform ${advancedExpandedTasks.has(task.id) ? "rotate-90" : ""}`}
                              />
                            </button>

                            {advancedExpandedTasks.has(task.id) && (
                              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2 flex items-center gap-1">
                                      Scheduling Strategy{" "}
                                      <InfoPopover content="Choose if this task should start as early as possible or as late as its dependencies allow." />
                                    </label>
                                    <div
                                      className={`flex bg-white dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-700 h-[42px] ${!isMaintainer ? "opacity-80 pointer-events-none" : ""}`}
                                    >
                                      <button
                                        onClick={() =>
                                          updateTask(task.id, {
                                            schedulingStrategy: "AEAP",
                                          })
                                        }
                                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition ${!task.schedulingStrategy || task.schedulingStrategy === "AEAP" ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                                        title="As Early As Possible"
                                      >
                                        <SkipBack size={12} /> Earliest
                                      </button>
                                      <button
                                        onClick={() =>
                                          updateTask(task.id, {
                                            schedulingStrategy: "ALAP",
                                          })
                                        }
                                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition ${task.schedulingStrategy === "ALAP" ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                                        title="As Late As Possible"
                                      >
                                        <SkipForward size={12} /> Latest
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2 flex items-center gap-1">
                                      Duration Mode{" "}
                                      <InfoPopover content="Set a fixed estimated duration, or calculate it automatically based on a target end time." />
                                    </label>
                                    <div
                                      className={`flex bg-white dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-700 h-[42px] ${!isMaintainer ? "opacity-80 pointer-events-none" : ""}`}
                                    >
                                      <button
                                        onClick={() =>
                                          updateTask(task.id, {
                                            durationStrategy:
                                              "ESTIMATED_DURATION",
                                          })
                                        }
                                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition ${!task.durationStrategy || task.durationStrategy === "ESTIMATED_DURATION" ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                                      >
                                        Duration
                                      </button>
                                      <button
                                        onClick={() =>
                                          updateTask(task.id, {
                                            durationStrategy: "FIXED_END_TIME",
                                            fixedEndTime: task.fixedEndTime || {
                                              time: "17:00",
                                              daysOffset: 0,
                                            },
                                          })
                                        }
                                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition ${task.durationStrategy === "FIXED_END_TIME" ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
                                      >
                                        End Time
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {task.durationStrategy === "FIXED_END_TIME" && (
                                  <div className="bg-slate-100 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 animate-in fade-in slide-in-from-top-2">
                                    <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-3">
                                      End Time Configuration
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                          Target End Time (HH:MM)
                                        </label>
                                        <input
                                          id={`end-time-${task.id}`}
                                          type="time"
                                          value={task.fixedEndTime?.time || ""}
                                          onChange={(e) =>
                                            updateTask(task.id, {
                                              fixedEndTime: {
                                                daysOffset:
                                                  task.fixedEndTime
                                                    ?.daysOffset || 0,
                                                time: e.target.value,
                                              },
                                            })
                                          }
                                          className="w-full text-sm p-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1">
                                          Days after plan start
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          value={
                                            task.fixedEndTime?.daysOffset || 0
                                          }
                                          onChange={(e) =>
                                            updateTask(task.id, {
                                              fixedEndTime: {
                                                time:
                                                  task.fixedEndTime?.time ||
                                                  "17:00",
                                                daysOffset:
                                                  parseInt(e.target.value) || 0,
                                              },
                                            })
                                          }
                                          className="w-full text-sm p-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        />
                                        <div className="text-[10px] text-slate-400 mt-1">
                                          0 = Same day, 1 = Next day, etc.
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AutomationConfigModal
        isOpen={automationModalOpen}
        onClose={() => {
          setAutomationModalOpen(false);
          setCurrentTaskForAutomation(null);
        }}
        orgId={currentOrg.id}
        onSave={handleAutomationSave}
        onRevert={
          currentTaskForAutomation?.type === TaskType.AUTOMATED
            ? handleAutomationRevert
            : undefined
        }
        initialRepo={currentTaskForAutomation?.githubRepo}
        initialWorkflow={currentTaskForAutomation?.githubWorkflow}
        initialBranch={currentTaskForAutomation?.githubBranch}
        initialInputs={currentTaskForAutomation?.githubInputs}
      />

      <GithubInstallModal
        isOpen={githubSetupModalOpen}
        onClose={() => setGithubSetupModalOpen(false)}
        orgId={currentOrg.id}
        githubInstallationId={currentOrg.githubInstallationId}
      />
    </div>
  );
};

export default PlanEditor;
